// ---------------------------------------------------------
// Logboek24 — Auditlog overzicht (beheerpagina, met filter + niveau)
// ---------------------------------------------------------
import React, { useEffect, useState } from "react";
import api from "../services/api";

export default function BeheerAuditLog() {
  const [logs, setLogs] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fout, setFout] = useState(null);
  const [zoekterm, setZoekterm] = useState("");
  const [actieFilter, setActieFilter] = useState("alle");

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const res = await api.get("/auditlogs", { params: { limit: 500 } });
        const data = Array.isArray(res.data) ? res.data : [];
        setLogs(data);
        setFiltered(data);
      } catch (err) {
        setFout(err?.response?.data?.message || err.message || "Kon auditlogs niet ophalen");
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, []);

  // 🔍 Filteren op zoekterm of actie
  useEffect(() => {
    let gefilterd = logs;

    if (actieFilter !== "alle") {
      gefilterd = gefilterd.filter((l) => l.actie === actieFilter);
    }

    if (zoekterm.trim() !== "") {
      const lower = zoekterm.toLowerCase();
      gefilterd = gefilterd.filter(
        (l) =>
          (l.userNaam && l.userNaam.toLowerCase().includes(lower)) ||
          (l.actie && l.actie.toLowerCase().includes(lower)) ||
          (l.objectType && l.objectType.toLowerCase().includes(lower)) ||
          (l.auditLevel && l.auditLevel.toLowerCase().includes(lower)) ||
          (l.ipAdres && l.ipAdres.toLowerCase().includes(lower))
      );
    }

    setFiltered(gefilterd);
  }, [zoekterm, actieFilter, logs]);

  if (loading) return <div className="p-6 text-gray-600">⏳ Auditlogs laden...</div>;
  if (fout) return <div className="p-6 text-red-600">❌ {fout}</div>;

  return (
    <div className="max-w-7xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-4">🧾 Auditlog – systeemactiviteiten</h1>
      <p className="text-gray-600 mb-6">
        Overzicht van acties, gebruikers en auditniveaus (basis / versterkt / forensisch).
      </p>

      {/* 🔹 Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <input
          type="text"
          placeholder="Zoek op gebruiker, actie, IP of niveau..."
          value={zoekterm}
          onChange={(e) => setZoekterm(e.target.value)}
          className="border rounded-md p-2 w-64"
        />
        <select
          value={actieFilter}
          onChange={(e) => setActieFilter(e.target.value)}
          className="border rounded-md p-2"
        >
          <option value="alle">Alle acties</option>
          <option value="survey_created">Survey aangemaakt</option>
          <option value="photo_uploaded">Foto geüpload</option>
          <option value="survey_completed">Survey afgerond</option>
          <option value="user_login">Gebruiker login</option>
          <option value="user_logout">Gebruiker logout</option>
          <option value="user_password_changed">Wachtwoord gewijzigd</option>
        </select>
        <button
          onClick={() => {
            setZoekterm("");
            setActieFilter("alle");
            setFiltered(logs);
          }}
          className="bg-gray-200 px-3 py-1 rounded hover:bg-gray-300"
        >
          Reset
        </button>
      </div>

      {/* 🔹 Tabel */}
      <div className="overflow-x-auto bg-white rounded-lg shadow">
        <table className="min-w-full border border-gray-200">
          <thead className="bg-gray-100 text-sm text-gray-700">
            <tr>
              <th className="py-2 px-3 text-left">Datum/tijd</th>
              <th className="py-2 px-3 text-left">Gebruiker</th>
              <th className="py-2 px-3 text-left">Actie</th>
              <th className="py-2 px-3 text-left">Niveau</th>
              <th className="py-2 px-3 text-left">Object</th>
              <th className="py-2 px-3 text-left">Tenant</th>
              <th className="py-2 px-3 text-left">IP</th>
              <th className="py-2 px-3 text-left">Device</th>
            </tr>
          </thead>
          <tbody className="text-sm text-gray-800">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center py-4 text-gray-500">
                  Geen auditrecords gevonden
                </td>
              </tr>
            ) : (
              filtered.map((log) => (
                <tr key={log._id} className="border-t hover:bg-gray-50">
                  <td className="py-2 px-3">{new Date(log.createdAt).toLocaleString()}</td>
                  <td className="py-2 px-3">{log.userNaam || "Onbekend"}</td>
                  <td className="py-2 px-3">{log.actie}</td>
                  <td
                    className={`py-2 px-3 font-semibold ${
                      log.auditLevel === "forensisch"
                        ? "text-red-600"
                        : log.auditLevel === "versterkt"
                        ? "text-yellow-600"
                        : "text-green-600"
                    }`}
                  >
                    {log.auditLevel || "-"}
                  </td>
                  <td className="py-2 px-3">
                    {log.objectType}{" "}
                    <span className="text-gray-400 text-xs">{log.objectId}</span>
                  </td>
                  <td className="py-2 px-3">{log.tenantId}</td>
                  <td className="py-2 px-3">{log.ipAdres}</td>
                  <td className="py-2 px-3 truncate max-w-xs">{log.userAgent}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
